import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash, FaFilter, FaTimes, FaLink } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import './Sessions.css';

const Sessions = () => {
  const { user }                              = useAuth();
  const isAdmin  = user?.role === 'admin' || user?.role === 'superadmin' || (user?.colleges?.length > 0);
  const isClerk   = user?.role === 'clerk';
  const isScoped  = user?.role === 'collegeadmin' || isClerk;
  const canCreate = isAdmin || isClerk || user?.role === 'collegeadmin';
  const canEdit   = isAdmin || user?.role === 'collegeadmin';
  const { colleges, loading: collegesLoading }                              = useColleges();
  const [sessions, setSessions]               = useState([]);
  const [sessionUsage, setSessionUsage]         = useState({}); // { sessionId: { hasData, counts } }
  const [loading, setLoading]                 = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [showSearchDrop, setShowSearchDrop]   = useState(false);
  const [filterCollege, setFilterCollege]     = useState('');
  const [filterSession, setFilterSession]     = useState('');
  const [filterStatus, setFilterStatus]       = useState('');
  const [showAddForm, setShowAddForm]         = useState(false);
  const [formName, setFormName]               = useState('');
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [editId, setEditId]                   = useState(null);
  const [expandedRow, setExpandedRow]         = useState(null);
  const [collegeSearch, setCollegeSearch]     = useState('');
  const [showCollegeDrop, setShowCollegeDrop] = useState(false);
  const collegeDropRef                        = useRef(null);
  const searchRef                             = useRef(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/sessions');
      const data = res.data.data || [];
      setSessions(data);
      // Check usage for all inactive sessions in parallel
      const inactive = data.filter(s => !s.isActive);
      const usageResults = await Promise.all(
        inactive.map(s => api.get(`/sessions/${s._id}/check`).then(r => ({ id: s._id, ...r.data })).catch(() => ({ id: s._id, hasData: false })))
      );
      const usageMap = {};
      usageResults.forEach(u => { usageMap[u.id] = { hasData: u.hasData, counts: u.counts }; });
      setSessionUsage(usageMap);
    } catch {
      Swal.fire('Error', 'Failed to load sessions.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (colleges.length === 1 && !editId)
      setSelectedColleges([colleges[0]._id]);
  }, [colleges]);

  useEffect(() => {
    const handler = (e) => {
      if (collegeDropRef.current && !collegeDropRef.current.contains(e.target)) setShowCollegeDrop(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredCollegeOptions = useMemo(() => {
    if (!collegeSearch) return colleges;
    const q = collegeSearch.toLowerCase();
    return colleges.filter(c => c.name.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q));
  }, [colleges, collegeSearch]);

  const allSelected = selectedColleges.length === colleges.length && colleges.length > 0;

  const toggleCollege = (id) => {
    setSelectedColleges(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedColleges(allSelected ? [] : colleges.map(c => c._id));
  };

  const selectedCollegeNames = useMemo(() => {
    if (selectedColleges.length === 0) return '-- Select College(s) --';
    if (selectedColleges.length === colleges.length && colleges.length > 0) return 'All Colleges';
    return colleges.filter(c => selectedColleges.includes(c._id)).map(c => c.name).join(', ');
  }, [selectedColleges, colleges]);

  const resetForm = () => {
    setFormName('');
    setSelectedColleges(colleges.length === 1 ? [colleges[0]._id] : []);
    setEditId(null);
    setShowAddForm(false);
    setCollegeSearch('');
    setShowCollegeDrop(false);
  };

  const validateForm = () => {
    const collegeIds = isScoped ? [user?.college?._id || user?.college] : selectedColleges;
    if (!formName.trim() || collegeIds.length === 0) {
      Swal.fire('Missing Fields', 'Please select a college and enter a session name.', 'warning');
      return false;
    }
    if (!/^\d{4}-\d{2}$/.test(formName.trim())) {
      Swal.fire('Invalid Format', 'Session name must be in format YYYY-YY (e.g. 2024-25).', 'warning');
      return false;
    }
    if (editId) {
      const duplicate = sessions.find(s =>
        s.name.trim().toLowerCase() === formName.trim().toLowerCase() &&
        String(s.college?._id || s.college) === String(collegeIds[0]) &&
        s._id !== editId
      );
      if (duplicate) {
        Swal.fire('Duplicate', `Session "${formName}" already exists for this college.`, 'warning');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const collegeIds = isScoped ? [user?.college?._id || user?.college] : selectedColleges;
      if (editId) {
        await api.put(`/sessions/${editId}`, { name: formName, college: collegeIds[0], isActive: false });
        Swal.fire('Updated!', 'Session updated.', 'success');
        resetForm();
        loadData();
      } else {
        await Promise.all(collegeIds.map(collegeId => api.post('/sessions', { name: formName, college: collegeId, isActive: false })));
        Swal.fire('Added!', `Created ${collegeIds.length} session(s).`, 'success');
        resetForm();
        loadData();
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save session.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEditForm = (sess) => {
    setEditId(sess._id);
    setFormName(sess.name);
    setSelectedColleges([sess.college?._id || sess.college || '']);
    setCollegeSearch('');
    setShowAddForm(true);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEdit = (sess) => {
    const usage = sessionUsage[sess._id];
    const counts = usage?.counts;
    const parts = [];
    if (counts?.students)      parts.push(`${counts.students} student${counts.students > 1 ? 's' : ''}`);
    if (counts?.payments)      parts.push(`${counts.payments} fee record${counts.payments > 1 ? 's' : ''}`);
    if (counts?.feeStructures) parts.push(`${counts.feeStructures} fee structure${counts.feeStructures > 1 ? 's' : ''}`);
    if (counts?.transport)     parts.push(`${counts.transport} transport allocation${counts.transport > 1 ? 's' : ''}`);
    const hasData = parts.length > 0;
    Swal.fire({
      title: 'Edit Session',
      html: `You are editing <b>"${sess.name}"</b>${hasData
        ? `<br/><br/>This session has: <b>${parts.join(', ')}</b><br/><span style='color:#64748b;font-size:0.9em'>Renaming is safe — all records link by ID, not by name.</span>`
        : '<br/><br/>This session has <b>no data yet</b>. You can safely rename or correct it.'}`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#3498db',
      confirmButtonText: 'Yes, edit',
      cancelButtonText: 'Cancel',
    }).then(result => { if (result.isConfirmed) openEditForm(sess); });
  };

  

  const handleDelete = async (sess) => {
    const usage = sessionUsage[sess._id];
    const counts = usage?.counts;
    const parts = [];
    if (counts?.students)      parts.push(`${counts.students} student${counts.students > 1 ? 's' : ''}`);
    if (counts?.payments)      parts.push(`${counts.payments} fee record${counts.payments > 1 ? 's' : ''}`);
    if (counts?.feeStructures) parts.push(`${counts.feeStructures} fee structure${counts.feeStructures > 1 ? 's' : ''}`);
    if (counts?.transport)     parts.push(`${counts.transport} transport allocation${counts.transport > 1 ? 's' : ''}`);
    const hasData = parts.length > 0;

    if (sess.isActive) {
      Swal.fire('Cannot Delete', 'Active session cannot be deleted. Activate another session first.', 'warning');
      return;
    }
    if (hasData) {
      Swal.fire({
        title: 'Cannot Delete',
        html: `<b>"${sess.name}"</b> is connected to:<br/><br/><b style="color:#e74c3c">${parts.join(', ')}</b><br/><br/>Remove all linked data first before deleting this session.`,
        icon: 'error',
        confirmButtonColor: '#e74c3c',
      });
      return;
    }
    const result = await Swal.fire({
      title: `Delete "${sess.name}"?`,
      text: 'This session has no data. It will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/sessions/${sess._id}`);
      setSessions(prev => prev.filter(s => s._id !== sess._id));
      Swal.fire('Deleted!', 'Session deleted.', 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
    }
  };

  const toggleStatus = async (sess) => {
    if (sess.isActive) {
      Swal.fire({
        title: 'Already Active',
        html: `<b>"${sess.name}"</b> is already the active session.<br/>To deactivate it, activate another session.`,
        icon: 'info',
        confirmButtonColor: '#3498db',
      });
      return;
    }
    const res = await Swal.fire({
      title: 'Activate Session?',
      html: `Set <b>"${sess.name}"</b> as active?<br/>The current active session will be deactivated automatically.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#27ae60',
      confirmButtonText: 'Yes, activate',
      cancelButtonText: 'Cancel',
    });
    if (!res.isConfirmed) return;
    try {
      await api.put(`/sessions/${sess._id}/activate`);
      loadData();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed.', 'error');
    }
  };

  const searchSuggestions = useMemo(() => {
    if (!searchQuery) return [];
    const q = searchQuery.toLowerCase();
    return sessions.filter(s =>
      s.name.toLowerCase().includes(q) || (s.college?.name || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [sessions, searchQuery]);

  const uniqueSessionNames = useMemo(() => {
    return [...new Set(sessions.map(s => s.name))].sort();
  }, [sessions]);

  const grouped = useMemo(() => {
    let result = sessions;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => s.name.toLowerCase().includes(q) || (s.college?.name || '').toLowerCase().includes(q));
    }
    if (filterCollege) result = result.filter(s => String(s.college?._id || s.college) === filterCollege);
    if (filterSession) result = result.filter(s => s.name === filterSession);
    if (filterStatus !== '') result = result.filter(s => s.isActive === (filterStatus === 'active'));
    const g = {};
    result.forEach(s => {
      const key = s.college?.name || 'Unknown College';
      if (!g[key]) g[key] = [];
      g[key].push(s);
    });
    return g;
  }, [sessions, searchQuery, filterCollege, filterSession, filterStatus]);

  return (
    <div className="session-layout-page-container">
      <h2 className="session-layout-page-title">Sessions</h2>

      <div className="session-layout-page-controls-wrapper">
        <div className="session-layout-page-filters-grid">

          {/* Search — admin only */}
          {isAdmin && (
            <div className="session-layout-page-search-box" ref={searchRef}>
              <FaSearch className="session-layout-page-search-icon" />
              <input
                type="text" placeholder="Search session or college..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowSearchDrop(true); }}
                onFocus={() => setShowSearchDrop(true)}
              />
              {searchQuery && (
                <button className="session-search-clear" onClick={() => { setSearchQuery(''); setShowSearchDrop(false); }}><FaTimes /></button>
              )}
              {showSearchDrop && searchSuggestions.length > 0 && (
                <div className="session-search-dropdown">
                  {searchSuggestions.map(s => (
                    <div key={s._id} className="session-search-item" onClick={() => { setSearchQuery(s.name); setShowSearchDrop(false); }}>
                      <span className="session-search-name">{s.name}</span>
                      <span className="session-search-college">{s.college?.name || ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* College filter — admin only */}
          {isAdmin && colleges.length > 1 && (
            <div className="session-layout-page-filter-box">
              <FaFilter className="session-layout-page-filter-icon" />
              <select value={filterCollege} onChange={e => setFilterCollege(e.target.value)}>
                <option value="">All Colleges</option>
                {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
          )}

          {/* Session name filter — everyone */}
          <div className="session-layout-page-filter-box">
            <FaFilter className="session-layout-page-filter-icon" />
            <select value={filterSession} onChange={e => setFilterSession(e.target.value)}>
              <option value="">All Sessions</option>
              {uniqueSessionNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          {/* Status filter — everyone */}
          <div className="session-layout-page-filter-box">
            <FaFilter className="session-layout-page-filter-icon" />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="disabled">Disabled Only</option>
            </select>
          </div>

        </div>

        {canCreate && (
          <button className="session-layout-page-btn session-layout-page-btn-add"
            onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
            <FaPlus /> {showAddForm ? 'Close Form' : 'Add Session'}
          </button>
        )}
      </div>

      {canCreate && showAddForm && (
        <div className="session-layout-page-inline-form">
          <h3 className="session-layout-page-form-title">{editId ? '✏️ Edit Session' : 'Add New Session'}</h3>
          <div className="session-layout-page-form-grid">
            <div className="session-layout-page-form-group">
              <label>College <span className="session-layout-page-req">*</span></label>
              {isScoped || colleges.length === 1 ? (
                <input value={colleges[0]?.name || 'Loading...'} disabled style={{ background: '#f1f5f9', padding: '9px 11px', border: '1px solid #ccc', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }} />
              ) : (
                <div className="cls-college-dropdown-wrapper" ref={collegeDropRef}>
                  <div className="cls-college-select-box" onClick={() => setShowCollegeDrop(v => !v)}>
                    <span style={{ color: selectedColleges.length ? '#1e293b' : '#94a3b8', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedCollegeNames}
                    </span>
                    <FaChevronDown style={{ fontSize: '0.75rem', color: '#64748b', flexShrink: 0 }} />
                  </div>
                  {showCollegeDrop && (
                    <div className="cls-college-dropdown">
                      <div className="cls-college-search">
                        <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem' }} />
                        <input type="text" placeholder="Search college..." value={collegeSearch} onChange={e => setCollegeSearch(e.target.value)} autoFocus />
                      </div>
                      {!collegeSearch && !editId && (
                        <div className="cls-college-option cls-select-all" onClick={toggleSelectAll}>
                          <input type="checkbox" checked={allSelected} readOnly />
                          <span>Select All</span>
                        </div>
                      )}
                      <div className="cls-college-options">
                        {filteredCollegeOptions.map(c => (
                          <div key={c._id} className="cls-college-option" onClick={() => editId ? setSelectedColleges([c._id]) : toggleCollege(c._id)}>
                            <input type={editId ? 'radio' : 'checkbox'} checked={selectedColleges.includes(c._id)} readOnly />
                            <span>{c.name}</span>
                            {c.code && <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 'auto' }}>{c.code}</span>}
                          </div>
                        ))}
                        {filteredCollegeOptions.length === 0 && (
                          <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>No colleges found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="session-layout-page-form-group">
              <label>Session Name <span className="session-layout-page-req">*</span></label>
              <input type="text" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. 2024-25" />
            </div>
          </div>
          {!editId && selectedColleges.length > 1 && (
            <div style={{ marginBottom: 14, padding: '10px 14px', background: '#eff6ff', borderRadius: 6, fontSize: '0.82rem', color: '#2563eb' }}>
              ℹ️ Will create <strong>{selectedColleges.length} separate sessions</strong> (one per college)
            </div>
          )}
          <div className="session-layout-page-form-buttons">
            <button className="session-layout-page-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="session-layout-page-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Session' : 'Save Session'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading sessions...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '8px' }}>No sessions found.</div>
      ) : (
        <div className="session-layout-page-grouped-container">
          {Object.entries(grouped).map(([collegeName, collegeSessions]) => (
            <div key={collegeName} className="session-layout-page-table-group">
              <h3 className="session-layout-page-group-header">🏢 {collegeName}</h3>
              <div className="session-layout-page-table-wrapper">
                <table className="session-layout-page-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th className="session-layout-page-align-left">Session</th>
                      <th className="session-table-col-college">College</th>
                      <th>Status</th>
                      {canEdit && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {collegeSessions.map((sess, i) => (
                      <React.Fragment key={sess._id}>
                        <tr className="session-layout-page-main-row">
                          <td>{i + 1}</td>
                          <td className="session-layout-page-align-left">
                            <div className="session-layout-page-name-wrapper"
                              onClick={() => setExpandedRow(expandedRow === sess._id ? null : sess._id)}
                              style={{ cursor: 'pointer' }}>
                              <span style={{ fontWeight: 700, color: '#2c3e50' }}>{sess.name}</span>
                              <button className="session-layout-page-expand-btn">
                                {expandedRow === sess._id ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            </div>
                          </td>
                          <td className="session-table-col-college">{sess.college?.name || collegeName}</td>
                          <td>
                            <label className="session-layout-page-switch">
                              <input type="checkbox" checked={sess.isActive} onChange={() => toggleStatus(sess)} />
                              <span className="session-layout-page-slider" />
                            </label>
                            <div style={{ fontSize: '0.7rem', marginTop: '4px', color: sess.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                              {sess.isActive ? 'Active' : 'Inactive'}
                            </div>
                          </td>
                          {canEdit && (
                            <td>
                              <div className="session-layout-page-action-btns">
                                {/* Connected badge */}
                                {sessionUsage[sess._id]?.hasData && (
                                  <span title={(() => {
                                    const c = sessionUsage[sess._id]?.counts;
                                    const p = [];
                                    if (c?.students)      p.push(`${c.students} student${c.students > 1 ? 's' : ''}`);
                                    if (c?.payments)      p.push(`${c.payments} fee record${c.payments > 1 ? 's' : ''}`);
                                    if (c?.feeStructures) p.push(`${c.feeStructures} fee structure${c.feeStructures > 1 ? 's' : ''}`);
                                    if (c?.transport)     p.push(`${c.transport} transport allocation${c.transport > 1 ? 's' : ''}`);
                                    return `Connected to: ${p.join(', ')}`;
                                  })()} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: 'help' }}>
                                    <FaLink style={{ fontSize: '0.65rem' }} /> Connected
                                  </span>
                                )}
                                {/* Edit — disabled if connected */}
                                <button
                                  className="session-layout-page-btn-edit"
                                  onClick={() => {
                                    if (sess.isActive || sessionUsage[sess._id]?.hasData) {
                                      if (sess.isActive) {
                                        Swal.fire('Cannot Edit', 'Active session cannot be edited. Deactivate it first by activating another session.', 'warning');
                                        return;
                                      }
                                      const c = sessionUsage[sess._id]?.counts;
                                      const p = [];
                                      if (c?.students)      p.push(`${c.students} student${c.students > 1 ? 's' : ''}`);
                                      if (c?.payments)      p.push(`${c.payments} fee record${c.payments > 1 ? 's' : ''}`);
                                      if (c?.feeStructures) p.push(`${c.feeStructures} fee structure${c.feeStructures > 1 ? 's' : ''}`);
                                      if (c?.transport)     p.push(`${c.transport} transport allocation${c.transport > 1 ? 's' : ''}`);
                                      Swal.fire({
                                        title: 'Cannot Edit',
                                        html: `<b>"${sess.name}"</b> is connected to:<br/><br/><b style="color:#d97706">${p.join(', ')}</b><br/><br/>Sessions with linked data cannot be edited.`,
                                        icon: 'warning',
                                        confirmButtonColor: '#d97706',
                                      });
                                      return;
                                    }
                                    openEditForm(sess);
                                  }}
                                  title={sess.isActive ? 'Cannot edit active session' : sessionUsage[sess._id]?.hasData ? 'Cannot edit — has linked data' : 'Edit'}
                                  style={{ opacity: (sess.isActive || sessionUsage[sess._id]?.hasData) ? 0.4 : 1, cursor: (sess.isActive || sessionUsage[sess._id]?.hasData) ? 'not-allowed' : 'pointer' }}
                                >
                                  <FaEdit />
                                </button>
                                {/* Delete — disabled if active or connected */}
                                <button
                                  className="session-layout-page-btn-delete"
                                  onClick={() => handleDelete(sess)}
                                  title={sess.isActive ? 'Cannot delete active session' : sessionUsage[sess._id]?.hasData ? 'Cannot delete — has linked data' : 'Delete'}
                                  style={{ opacity: (sess.isActive || sessionUsage[sess._id]?.hasData) ? 0.4 : 1, cursor: (sess.isActive || sessionUsage[sess._id]?.hasData) ? 'not-allowed' : 'pointer' }}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                        {expandedRow === sess._id && (
                          <tr className="session-layout-page-expanded-row">
                            <td colSpan={canEdit ? 5 : 4}>
                              <div className="session-layout-page-expanded-content">
                                <div className="session-layout-page-expanded-item session-exp-college">
                                  <span className="session-layout-page-exp-label">College</span>
                                  <span className="session-layout-page-exp-value">{sess.college?.name || collegeName}</span>
                                </div>
                                <div className="session-layout-page-expanded-item session-exp-status">
                                  <span className="session-layout-page-exp-label">Status</span>
                                  <span className="session-layout-page-exp-value" style={{ color: sess.isActive ? '#27ae60' : '#e74c3c', fontWeight: 700 }}>
                                    {sess.isActive ? 'Active' : 'Disabled'}
                                  </span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Sessions;
